// Tenant Details Tab - Edit tenant information
import React, { useState, useEffect } from 'react';
import { useOnboarding } from '../../context/OnboardingContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Loader2, Save, Building2, Upload, X, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const TenantDetailsTab = ({ tenantId }) => {
  const { completeItem } = useOnboarding();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreviewOpen, setLogoPreviewOpen] = useState(false);
  const [logoBlob, setLogoBlob] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [form, setForm] = useState({
    name: '',
    email: '',
    reply_to_email: '',
    phone: '',
    address_street: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    website: '',
    logo_url: '',
    default_currency: 'USD',
    signatory_user_id: '',
    signatory_name: '',
    signatory_position: ''
  });

  useEffect(() => {
    fetchTenant();
    fetchUsers();
  }, [tenantId]);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setTenantUsers(res.data || []);
    } catch { /* ignore */ }
  };

  const fetchTenant = async () => {
    try {
      const response = await api.get(`/tenants/${tenantId}`);
      const data = response.data;
      setTenant(data);
      setForm({
        name: data.name || '',
        email: data.email || '',
        reply_to_email: data.reply_to_email || '',
        phone: data.phone || '',
        address_street: data.address_street || '',
        address_city: data.address_city || '',
        address_state: data.address_state || '',
        address_zip: data.address_zip || '',
        website: data.website || '',
        logo_url: data.logo_url || '',
        default_currency: data.default_currency || 'USD',
        signatory_user_id: data.settings?.signatory_user_id || '',
        signatory_name: data.settings?.signatory_name || '',
        signatory_position: data.settings?.signatory_position || ''
      });
      
      // Fetch logo with authentication if URL exists
      if (data.logo_url) {
        await fetchLogoBlob(data.logo_url);
      }
    } catch (error) {
      console.error('Error fetching tenant:', error);
      toast.error('Failed to load tenant details');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogoBlob = async (logoUrl) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(logoUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setLogoBlob(blobUrl);
      } else {
        console.error('Failed to load logo:', response.status);
        setLogoBlob(null);
      }
    } catch (error) {
      console.error('Error loading logo:', error);
      setLogoBlob(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/tenants/${tenantId}`, {
        name: form.name,
        email: form.email,
        reply_to_email: form.reply_to_email,
        phone: form.phone,
        address_street: form.address_street,
        address_city: form.address_city,
        address_state: form.address_state,
        address_zip: form.address_zip,
        website: form.website,
        logo_url: form.logo_url,
        default_currency: form.default_currency,
        settings: {
          ...tenant?.settings,
          signatory_user_id: form.signatory_user_id,
          signatory_name: form.signatory_name,
          signatory_position: form.signatory_position
        }
      });
      toast.success('Tenant details updated');
      completeItem('setup_branding');
      fetchTenant();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save tenant details');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      // Create preview immediately
      const previewUrl = URL.createObjectURL(file);
      setLogoBlob(previewUrl);

      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'tenant_logo');

      const response = await fetch(
        `${window.location.origin}/api/files/upload`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        }
      );

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      const fileUrl = `${window.location.origin}/api/files/download/${data.id}`;

      // Update form state
      setForm(prev => ({...prev, logo_url: fileUrl}));
      
      // Auto-save the logo to tenant immediately
      await api.put(`/tenants/${tenantId}`, {
        logo_url: fileUrl
      });
      
      // Fetch authenticated version for display
      await fetchLogoBlob(fileUrl);
      
      toast.success('Logo uploaded and saved');
      completeItem('setup_branding');
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error('Failed to upload logo');
      setLogoBlob(null);
    } finally {
      setUploadingLogo(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            {form.logo_url && logoBlob ? (
              <img 
                src={logoBlob} 
                alt="Logo" 
                className="h-14 w-14 object-contain rounded border cursor-pointer" 
                onClick={() => setLogoPreviewOpen(true)}
              />
            ) : (
              <div className="h-14 w-14 rounded border flex items-center justify-center bg-muted">
                <Building2 className="w-6 h-6 text-muted-foreground" />
              </div>
            )}
            <div>
              <CardTitle className="flex items-center gap-2">
                Organization Details
              </CardTitle>
              {form.name && <p className="text-sm text-muted-foreground">{form.name}</p>}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Organization Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                placeholder="Your organization name"
              />
            </div>
            <div className="space-y-2">
              <Label>Billing Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({...form, email: e.target.value})}
                placeholder="billing@example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Reply To Email</Label>
              <Input
                type="email"
                value={form.reply_to_email}
                onChange={(e) => setForm({...form, reply_to_email: e.target.value})}
                placeholder="reply@example.com"
              />
              <p className="text-xs text-muted-foreground">
                Default reply-to address for reports, statements, and other emails (invoices use Billing Email)
              </p>
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({...form, phone: e.target.value})}
                placeholder="(555) 123-4567"
              />
            </div>
            <div className="space-y-2">
              <Label>Default Currency</Label>
              <Select
                value={form.default_currency}
                onValueChange={(value) => setForm({...form, default_currency: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                  <SelectItem value="CAD">CAD ($)</SelectItem>
                  <SelectItem value="AUD">AUD ($)</SelectItem>
                  <SelectItem value="JPY">JPY (¥)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Used throughout invoices, pricing, and reports
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Website</Label>
            <Input
              value={form.website}
              onChange={(e) => setForm({...form, website: e.target.value})}
              placeholder="https://example.com"
            />
          </div>

          <div className="space-y-2">
            <Label>Street Address</Label>
            <Input
              value={form.address_street}
              onChange={(e) => setForm({...form, address_street: e.target.value})}
              placeholder="123 Main Street"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>City</Label>
              <Input
                value={form.address_city}
                onChange={(e) => setForm({...form, address_city: e.target.value})}
                placeholder="City"
              />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Input
                value={form.address_state}
                onChange={(e) => setForm({...form, address_state: e.target.value})}
                placeholder="State"
              />
            </div>
            <div className="space-y-2">
              <Label>ZIP</Label>
              <Input
                value={form.address_zip}
                onChange={(e) => setForm({...form, address_zip: e.target.value})}
                placeholder="ZIP"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Organization Logo</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById('logo-upload-input').click()}
                disabled={uploadingLogo}
                className="flex-1"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploadingLogo ? 'Uploading...' : form.logo_url ? 'Change Logo' : 'Upload Logo'}
              </Button>
              <input
                id="logo-upload-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleLogoUpload(file);
                    e.target.value = '';
                  }
                }}
              />
            </div>
            {form.logo_url && logoBlob && (
              <div 
                className="mt-3 p-3 border rounded hover:border-primary cursor-pointer transition-colors" 
                onClick={() => setLogoPreviewOpen(true)}
              >
                <img 
                  src={logoBlob} 
                  alt="Logo thumbnail" 
                  className="h-24 object-contain mx-auto" 
                />
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Upload your organization logo (max 5MB, JPG/PNG)
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Contract Signatories</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Signatory</Label>
              <Select
                value={form.signatory_user_id}
                onValueChange={(val) => {
                  const user = tenantUsers.find(u => u.id === val);
                  setForm({
                    ...form,
                    signatory_user_id: val,
                    signatory_name: user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : '',
                    signatory_position: form.signatory_position
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select signatory user" />
                </SelectTrigger>
                <SelectContent>
                  {tenantUsers.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Position/Title</Label>
              <Input
                value={form.signatory_position}
                onChange={(e) => setForm({...form, signatory_position: e.target.value})}
                placeholder="e.g., CEO, Manager"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      {/* Logo Preview Dialog */}
      <Dialog open={logoPreviewOpen} onOpenChange={setLogoPreviewOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Organization Logo</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {logoBlob && (
              <div className="flex items-center justify-center bg-muted rounded-lg p-8">
                <img 
                  src={logoBlob} 
                  alt="Logo preview" 
                  className="max-h-96 max-w-full object-contain" 
                />
              </div>
            )}
          </div>
          <DialogFooter className="flex justify-between sm:justify-between">
            <Button
              variant="outline"
              onClick={() => {
                setForm(prev => ({...prev, logo_url: ''}));
                setLogoBlob(null);
                setLogoPreviewOpen(false);
                toast.success('Logo removed');
              }}
            >
              <X className="w-4 h-4 mr-2" />
              Remove Logo
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (logoBlob) {
                  const link = document.createElement('a');
                  link.href = logoBlob;
                  link.download = 'logo.png';
                  link.click();
                }
              }}
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantDetailsTab;
